import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import {
   combineReportRowRisks,
   getReportRowRiskSortRank
} from '../utils/report-risk.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

function formatAmount(value: unknown): string {
   const amount = asNumberOrNull(value);

   if (amount === null) {
      return '';
   }

   return Math.abs(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });
}

function getGuestCreditLimitRows(reportJson: unknown): any[] {
   return getReportArray(reportJson, 'GuestCreditLimit');
}

function getRowId(row: any): string {
   return (
      getRealTyallsRowId(row) ||
      `gcl:${asString(row?.ReservationId)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.FolioWindowNo)}`
   );
}

function getReservationGroupKey(row: any): string {
   return (
      asString(row?.ReservationId) ||
      asString(row?.ConfirmationNumber) ||
      `${asString(row?.RoomNumber)}:${asString(row?.Name)}`
   );
}

function getFolioWindowNo(row: any): string {
   return asString(row?.FolioWindowNo);
}

function getFolioWindowSortValue(row: any): number {
   const windowNo = Number(row?.FolioWindowNo);

   return Number.isFinite(windowNo) ? windowNo : 9999;
}

function getPaymentMethod(row: any): string {
   return asString(row?.PaymentMethod).toUpperCase();
}

function getPayee(row: any): string {
   return asString(row?.Payee) || asString(row?.RoutingInstructions) || '-';
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function getCreditLimit(row: any): number | null {
   return asNumberOrNull(row?.CreditLimit);
}

function getTotalPositiveBalance(rows: any[]): number {
   return rows.reduce((total, row) => {
      const balance = getBalance(row);

      return balance !== null && balance > 0 ? total + balance : total;
   }, 0);
}

function getSummaryLimit(rows: any[]): number | null {
   const limits = rows
      .map((row) => getCreditLimit(row))
      .filter((limit): limit is number => limit !== null);

   return limits.length ? Math.max(...limits) : null;
}

function getSummaryVariance(rows: any[]): number | null {
   const summaryLimit = getSummaryLimit(rows);

   return summaryLimit === null ? null : summaryLimit - getTotalPositiveBalance(rows);
}

function joinUnique(values: string[], separator = '\n'): string {
   return Array.from(
      new Set(values.map((value) => value.trim()).filter(Boolean))
   ).join(separator);
}

function formatWindowPaymentWithBalance(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const paymentMethod = getPaymentMethod(row) || '-';
         const balance = formatAmount(getBalance(row));

         return windowNo
            ? `${windowNo}: ${paymentMethod} [${balance}]`
            : `${paymentMethod} [${balance}]`;
      })
   );
}

function formatWindowPayees(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const payee = getPayee(row);

         return windowNo ? `${windowNo}: ${payee}` : payee;
      })
   );
}

function groupRowsByReservation(rows: any[]): any[][] {
   const groups = new Map<string, any[]>();

   for (const row of rows) {
      const key = getReservationGroupKey(row);

      if (!key) {
         continue;
      }

      const existingRows = groups.get(key) ?? [];

      existingRows.push(row);
      groups.set(key, existingRows);
   }

   return Array.from(groups.values())
      .map((groupRows) =>
         [...groupRows].sort(
            (a, b) => getFolioWindowSortValue(a) - getFolioWindowSortValue(b)
         )
      )
      .sort((a, b) => {
         const firstA = a[0];
         const firstB = b[0];

         return (
            asString(firstA?.RoomNumber).localeCompare(
               asString(firstB?.RoomNumber)
            ) || asString(firstA?.Name).localeCompare(asString(firstB?.Name))
         );
      });
}

function getGroupRowId(rows: any[]): string {
   const first = rows[0];

   return (
      asString(first?.ReservationId) ||
      asString(first?.ConfirmationNumber) ||
      getRowId(first)
   );
}

function getSourceRowIds(rows: any[]): string[] {
   return Array.from(
      new Set(rows.map((row) => getRealTyallsRowId(row)).filter(Boolean))
   );
}

function getRiskDisplay(rows: any[]): string {
   const risk = combineReportRowRisks(rows);

   if (!risk) {
      return '';
   }

   return (risk.messages.length ? risk.messages : risk.riskCodes).join('\n');
}

function flattenRows(reportJson: unknown): ReportRowVm[] {
   const groups = groupRowsByReservation(getGuestCreditLimitRows(reportJson));

   return groups.map((groupRows) => {
      const first = groupRows[0];
      const totalBalance = getTotalPositiveBalance(groupRows);
      const summaryLimit = getSummaryLimit(groupRows);
      const summaryVariance = getSummaryVariance(groupRows);
      const risk = combineReportRowRisks(groupRows);
      const alert = getRiskDisplay(groupRows);

      return {
         rowId: `guest-credit-limit:${getGroupRowId(groupRows)}`,
         sourceRowIds: getSourceRowIds(groupRows),
         cells: {
            rowType: 'guestCreditLimit',
            room: asString(first?.RoomNumber),
            guestName: asString(first?.Name),
            noPost: asString(first?.NoPost),
            paymentWindows: formatWindowPaymentWithBalance(groupRows),
            payee: formatWindowPayees(groupRows),
            limitAndAuth: formatCurrency(summaryLimit),
            limitAndAuthRaw: summaryLimit,
            balance: formatCurrency(totalBalance),
            balanceRaw: totalBalance,
            variance: formatCurrency(summaryVariance),
            varianceRaw: summaryVariance,
            alert,
            alertRaw: alert,
            alertSortRank: getReportRowRiskSortRank(risk),
            reservationId: asString(first?.ReservationId),
            confirmationNumber: asString(first?.ConfirmationNumber)
         }
      };
   });
}

export const GUEST_CREDIT_LIMIT_DEFINITION: ReportDefinition = {
   reportType: 'GuestCreditLimit',
   version: 6,
   title: 'Guest Credit Limit',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Guest Credit Limit',
      snippets: ({ allRows }) => [
         {
            label: 'Reservations',
            value: allRows.filter(
               (row) => row.cells?.['rowType'] === 'guestCreditLimit'
            ).length
         }
      ]
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['guestCreditLimit']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['guestCreditLimit']
   },

   columns: [
      {
         key: 'room',
         header: 'ROOM',
         width: '76px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '220px',
         maxChars: 70,
         sort: { type: 'string' }
      },
      {
         key: 'noPost',
         header: 'POST',
         width: '70px',
         sort: { type: 'string' }
      },
      {
         key: 'paymentWindows',
         header: 'WIN: PAY MTH [BAL]',
         width: '180px',
         sort: { type: 'string' }
      },
      {
         key: 'payee',
         header: 'PAYEE',
         width: '280px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'limitAndAuth',
         header: 'LIMIT + AUTH',
         width: '130px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['limitAndAuthRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'balance',
         header: 'BALANCE',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['balanceRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'variance',
         header: 'VARIANCE',
         width: '90px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['varianceRaw']
         }
      },
      {
         key: 'alert',
         header: 'RISK',
         width: '320px',
         maxChars: 160,
         sort: {
            type: 'number',
            getValue: (row) => row.cells['alertSortRank']
         }
      }
   ],

   flattenRows,

   filter: {
      fields: [
         {
            key: 'room',
            label: 'Room',
            type: 'string'
         },
         {
            key: 'guestName',
            label: 'Guest Name',
            type: 'string'
         },
         {
            key: 'noPost',
            label: 'No Post',
            type: 'string'
         },
         {
            key: 'paymentWindows',
            label: 'Payment Method',
            type: 'string'
         },
         {
            key: 'payee',
            label: 'Payee',
            type: 'string'
         },
         {
            key: 'alert',
            label: 'Risk',
            type: 'string',
            getValue: (row) => row.cells['alertRaw']
         },
         {
            key: 'balance',
            label: 'Balance',
            type: 'number',
            getValue: (row) => row.cells['balanceRaw']
         },
         {
            key: 'variance',
            label: 'Variance',
            type: 'number',
            getValue: (row) => row.cells['varianceRaw']
         }
      ]
   }
};
